import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Button,
} from "@mui/material";
import { Medication, Visibility } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useNavigate } from "react-router-dom";

const ActivePrescriptions = ({ patientId }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  // Fetch recent prescriptions (last 30 days, not paid or pending)
  const {
    data: prescriptionData,
    isLoading,
    isError,
  } = useFetchObjects(
    patientId ? ["prescriptions", patientId, "recent"] : null,
    patientId ? `prescriptions/?patient=${patientId}` : "",
    token
  );

  const prescriptions = prescriptionData?.results || [];

  // Filter to show recent prescriptions (last 30 days)
  const recentPrescriptions = prescriptions
    .filter((prescription) => {
      if (!prescription.date && !prescription.created_at) return false;
      const prescriptionDate = new Date(
        prescription.date || prescription.created_at
      );
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return prescriptionDate >= thirtyDaysAgo;
    })
    .slice(0, 5); // Show only last 5

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("profile.loading") || "Loading..."}
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t("profile.errorLoadingPrescriptions") ||
          "Error loading prescriptions"}
      </Alert>
    );
  }

  if (recentPrescriptions.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("profile.noRecentPrescriptions") || "No recent prescriptions"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          {t("profile.recentPrescriptions") || "Recent Prescriptions"}
        </Typography>
        <Button
          size="small"
          startIcon={<Visibility />}
          onClick={() => {
            // Navigate to prescriptions view or show all
            navigate(`/patient-profile/prescriptions`);
          }}
        >
          {t("profile.viewAll") || "View All"}
        </Button>
      </Box>
      <Grid container spacing={2}>
        {recentPrescriptions.map((prescription) => (
          <Grid item xs={12} key={prescription.id}>
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Medication color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t("profile.prescription") || "Prescription"} #
                        {prescription.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("profile.date") || "Date"}:{" "}
                        {new Date(
                          prescription.date || prescription.created_at
                        ).toLocaleDateString()}
                      </Typography>
                      {(prescription.doctor_display ||
                        (prescription.doctor &&
                          typeof prescription.doctor === "object")) && (
                        <Typography variant="body2" color="text.secondary">
                          {t("profile.doctor") || "Doctor"}:{" "}
                          {prescription.doctor_display ||
                            (prescription.doctor?.first_name &&
                              `${prescription.doctor.first_name} ${prescription.doctor.last_name || ""}`.trim()) ||
                            (typeof prescription.doctor === "number"
                              ? `ID: ${prescription.doctor}`
                              : "N/A")}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {t("profile.medicines") || "Medicines"}:{" "}
                        {Array.isArray(prescription.drugs)
                          ? prescription.drugs.length
                          : prescription.drugs?.length || 0}{" "}
                        {t("profile.items") || "items"}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={
                      prescription.payment_status === 2
                        ? t("profile.paid") || "Paid"
                        : t("profile.pending") || "Pending"
                    }
                    color={
                      prescription.payment_status === 2 ? "success" : "warning"
                    }
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ActivePrescriptions;
